import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import {
  MAT_LEGACY_TOOLTIP_DEFAULT_OPTIONS as MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatLegacyTooltipDefaultOptions as MatTooltipDefaultOptions
} from '@angular/material/legacy-tooltip';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import {
  routerReducer,
  RouterStateSerializer,
  StoreRouterConnectingModule
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SharedAuditModule } from '@sd-portal/shared/audit';
import { DashboardComponent } from '@sd-portal/shared/dashboard';
import { SHARED_ENVIRONMENT } from '@sd-portal/shared/environment';
import { SharedNavigationModule } from '@sd-portal/shared/navigation';
import { PermissionsModule, PermissionsService } from '@sd-portal/shared/permissions';
import {
  AppMonitoringService,
  AuthHttpInterceptor,
  GlobalErrorHandler,
  PostLoginComponent,
  SdApiHttpInterceptor,
  SdErrorHttpInterceptor,
  ShellModule
} from '@sd-portal/shared/shell';
import { SharedUserConfigurationModule } from '@sd-portal/shared/user-configuration';
import { SdRouterStateSerializer, UtilModule } from '@sd-portal/shared/util';
import * as Sentry from '@sentry/angular';
import {
  AbstractSecurityStorage,
  AuthModule,
  DefaultLocalStorageService,
  LogLevel,
  OidcSecurityService
} from 'angular-auth-oidc-client';
import { ToastrModule as ToasterModule } from 'ngx-toastr';
import { TrainingVideosComponent } from '@sd-portal/training-videos';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CsatModule } from '@sd-portal/shared/csat';

export const customTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 500,
  disableTooltipInteractivity: true
};

// copy of environment to use in module declaration since function expression are disallowed in decorators
const environmentCopy = Object.assign({}, { production: environment.production });

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AuthModule.forRoot({
      config: {
        authority: environment.oidcsettings.stsServer,
        redirectUrl: environment.oidcsettings.redirect_url,
        clientId: environment.oidcsettings.client_id,
        responseType: environment.oidcsettings.response_type,
        scope: environment.oidcsettings.scope,
        hdParam: environment.oidcsettings.hd_param,
        startCheckSession: environment.oidcsettings.start_checksession,
        silentRenew: environment.oidcsettings.silent_renew,
        silentRenewUrl: environment.oidcsettings.silent_renew_url,
        renewTimeBeforeTokenExpiresInSeconds:
          environment.oidcsettings.silent_renew_offset_in_seconds,
        useRefreshToken: environment.oidcsettings.useRefreshToken,
        triggerRefreshWhenIdTokenExpired:
          environment.oidcsettings.triggerRefresh_WhenIdTokenExpired,
        ignoreNonceAfterRefresh: environment.oidcsettings.ignoreNonceAfterRefresh,
        postLoginRoute: environment.oidcsettings.post_login_route,
        postLogoutRedirectUri: environment.oidcsettings.post_logout_redirect_uri,
        forbiddenRoute: environment.oidcsettings.forbidden_route,
        unauthorizedRoute: environment.oidcsettings.unauthorized_route,
        autoUserInfo: environment.oidcsettings.auto_userinfo,
        logLevel: LogLevel.Error,
        autoCleanStateAfterAuthentication:
          environment.oidcsettings.auto_clean_state_after_authentication,
        maxIdTokenIatOffsetAllowedInSeconds:
          environment.oidcsettings.max_id_token_iat_offset_allowed_in_seconds,
        triggerAuthorizationResultEvent: environment.oidcsettings.trigger_authorization_result_event
      }
    }),
    ShellModule,
    AppRoutingModule,
    StoreModule.forRoot(
      { router: routerReducer },
      {
        metaReducers: !environmentCopy.production ? [] : [],
        runtimeChecks: { strictStateImmutability: true, strictActionImmutability: true }
      }
    ),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 40, // Retains last 40 states
      logOnly: environmentCopy.production // Restrict extension to log-only mode
    }),
    PermissionsModule,
    SharedUserConfigurationModule,
    CsatModule,
    SharedAuditModule,
    SharedNavigationModule,
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router'
    }),
    UtilModule,
    ToasterModule.forRoot({
      closeButton: true,
      timeOut: 5000,
      newestOnTop: true,
      progressAnimation: 'decreasing',
      positionClass: 'toast-bottom-right',
      toastClass: 'ngx-toastr'
    }),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environmentCopy.production }),
    HammerModule
  ],
  declarations: [AppComponent, DashboardComponent, PostLoginComponent, TrainingVideosComponent],
  providers: [
    { provide: SHARED_ENVIRONMENT, useValue: environment },
    { provide: HTTP_INTERCEPTORS, useClass: SdApiHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SdErrorHttpInterceptor, multi: true },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: customTooltipDefaults },
    { provide: RouterStateSerializer, useClass: SdRouterStateSerializer },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: AbstractSecurityStorage, useClass: DefaultLocalStorageService },
    OidcSecurityService,
    PermissionsService,
    AppMonitoringService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
